import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin")({
  head: () => ({ meta: [{ title: "Admin — Truck Company Center" }, { name: "robots", content: "noindex" }] }),
  component: AdminGate,
});

function AdminGate() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  async function refresh() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { setUser(null); setIsAdmin(false); setLoading(false); return; }
    setUser(session.user);
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", session.user.id).eq("role", "admin").maybeSingle();
    setIsAdmin(!!data);
    setLoading(false);
  }
  useEffect(() => {
    const sub = supabase.auth.onAuthStateChange(() => { refresh(); });
    refresh();
    return () => { sub.data.subscription.unsubscribe(); };
  }, []);

  async function logout() { await supabase.auth.signOut(); nav({ to: "/cuenta/login" }); }

  // Effect-based redirects (no side effects in render)
  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav({ to: "/cuenta/login", search: { redirect: "/admin" } as any });
      return;
    }
    if (!isAdmin) {
      const t = setTimeout(() => { nav({ to: "/cuenta" }); }, 1500);
      return () => clearTimeout(t);
    }
  }, [loading, user, isAdmin, nav]);

  if (loading) return <div className="mx-auto max-w-2xl px-4 py-20 text-muted-foreground">Cargando...</div>;

  if (!user) {
    return <div className="mx-auto max-w-2xl px-4 py-20 text-muted-foreground">Redirigiendo a inicio de sesión…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-semibold text-foreground">Acceso restringido</h1>
        <p className="mt-2 text-muted-foreground">Tu cuenta {user.email} no tiene permisos de administrador. Te redirigimos a tu cuenta…</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/cuenta" className="rounded-md border border-hairline bg-surface px-4 py-2 text-sm text-foreground hover:bg-surface-2">Ir a mi cuenta ahora</Link>
          <Button onClick={logout} variant="outline" className="border-hairline">Cerrar sesión</Button>
        </div>
      </div>
    );
  }


  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-foreground">Admin</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{user.email}</span>
          <Button onClick={logout} variant="outline" size="sm" className="border-hairline">Salir</Button>
        </div>
      </div>
      <nav className="mt-6 flex flex-wrap gap-2 border-b border-hairline pb-4">
        <Tab to="/admin">Dashboard</Tab>
        <Tab to="/admin/pedidos">Pedidos</Tab>
        <Tab to="/admin/clientes">Clientes</Tab>
        <Tab to="/admin/intakes">Formularios</Tab>
        <Tab to="/admin/paquetes">Paquetes</Tab>
        <Tab to="/admin/servicios">Servicios</Tab>
        <Tab to="/admin/faq">FAQ</Tab>
        <Tab to="/admin/settings">Settings</Tab>
      </nav>
      <div className="mt-8"><Outlet /></div>
    </div>
  );
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return <Link to={to as any} className="rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-foreground hover:bg-surface-2 [&.active]:border-primary [&.active]:bg-primary/10 [&.active]:text-primary" activeProps={{ className: "active" }} activeOptions={{ exact: true }}>{children}</Link>;
}
